import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getAccountBalance, getWithdrawalAccount } from '@/api/wallet';
import { navigateByKey, redirectByKey } from '@/router/routes';
import { PAYMENT_ICONS } from '@/service/payment';
import { confirm } from '@/utils/modal';

export type WithdrawAccount = API.WithdrawAccount & {
  icon: string
};

const WithdrawWay = {
  0: 'balance',
  1: 'alipay',
  2: 'wechat',
};

export const useWalletStore = defineStore('wallet', () => {
  // ====== 余额相关 ======
  /** 账户余额 */
  const accountBalance = ref<number | string>('--');
  /** 待提现余额 */
  const dtxBalance = ref(0);
  /** 云仓余额 */
  const cloudBalance = ref(0);

  // ====== 提现账号相关 ======
  /** 提现账号绑定状态：0 未绑定，1 已绑定 */
  const withdrawalAccountStatus = ref(0);
  /** 提现账号信息 */
  const withdrawalAccount = ref<Partial<WithdrawAccount>>();

  // ====== Actions ======
  /** 刷新账户余额 */
  async function refreshBalance() {
    try {
      const res: any = await getAccountBalance();
      accountBalance.value = res?.data?.accountBalance ?? res?.accountBalance ?? '--';
      dtxBalance.value = res?.data?.dtxBalance ?? res?.dtxBalance ?? 0;
      cloudBalance.value = res?.data?.cloudBalance ?? res?.cloudBalance ?? 0;
    }
    catch {
      accountBalance.value = '--';
      dtxBalance.value = 0;
    }
  }

  /** 刷新提现账号信息 */
  async function refreshWithdrawalAccount() {
    try {
      const res = await getWithdrawalAccount();
      // 当前只允许提现到支付宝，所以只能检测是否绑定了支付宝账号
      const accountItem = res.find(item => item.payType === 1 && item.payId && item.payName);
      withdrawalAccountStatus.value = accountItem ? 1 : 0;
      if (withdrawalAccountStatus.value === 1) {
        withdrawalAccount.value = {
          ...accountItem,
          icon: PAYMENT_ICONS[WithdrawWay[accountItem.payType]],
        };
      }
      else {
        withdrawalAccount.value = null;
      }
    }
    catch {
      withdrawalAccountStatus.value = 0;
      withdrawalAccount.value = null;
    }
  }

  /**
   * 检测提现账号是否已绑定，未绑定时弹框引导
   * @returns 是否已绑定
   */
  async function checkWithdrawalAccount(isWithdrawal: boolean = false): Promise<boolean> {
    await refreshWithdrawalAccount();
    if (withdrawalAccountStatus.value !== 1) {
      const result = await confirm('温馨提示', '您还未设置提现账号，请先绑定提现账号再进行提现！', '去设置');
      if (result === 'confirm') {
        if (isWithdrawal) {
          redirectByKey('mineWalletWithdrawAccount');
        }
        else {
          navigateByKey('mineWalletWithdrawAccount');
        }
      }
      return false;
    }
    return true;
  }

  return {
    accountBalance,
    dtxBalance,
    cloudBalance,
    withdrawalAccountStatus,
    withdrawalAccount,
    refreshBalance,
    refreshWithdrawalAccount,
    checkWithdrawalAccount,
  };
});
